import { Info } from 'lucide-react';
import {
    Fragment,
    type ReactNode,
    type Ref,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import { trackVariableDragOutside } from '@apps/web/components/shared/messages/variable_drag_out';
import { snapVariableDropToWord } from '@apps/web/components/shared/messages/variable_word_drop';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { type TemplateVariableOption } from '@apps/web/lib/template_variables';
import { cn } from '@apps/web/lib/utils';

// A template variable that can be embedded as an inline pill. The stored
// message keeps the `{token}` form (e.g. `{caregiver_name}`); the pill is
// purely a presentation layer over that string so the backend
// substitution at send time is unchanged.
export type TemplateVariable = TemplateVariableOption;

// Normalizes a list of variable tokens (with or without surrounding `{}`
// braces) into the `{token, label}` shape the editor consumes. The label
// humanizes the token, e.g. `caregiver_first_name` -> `caregiver first
// name`, so existing `string[]` variable lists can drive the pill UX
// without restating each entry as a `TemplateVariable`.
export function toTemplateVariables(
    tokens: ReadonlyArray<string>,
): ReadonlyArray<TemplateVariable> {
    return tokens.map((raw) => {
        const token = raw.replace(/[{}]/g, '');
        return { token, label: token.split('_').join(' ') };
    });
}

// dataset key (`data-variable`) marking a pill span and carrying its token.
const VARIABLE_DATA_KEY = 'variable';
const VARIABLE_LABEL_DATA_KEY = 'variableLabel';
const VARIABLE_SEPARATORS_DATA_KEY = 'variableSeparators';
const GENERATED_SEPARATOR_DATA_KEY = 'generatedVariableSeparator';
const DROP_PREVIEW_DATA_KEY = 'variableDropPreview';
export const TEMPLATE_VARIABLE_DRAG_TYPE =
    'application/x-phoebe-template-variable';
const HISTORY_LIMIT = 100;

// Block-level wrappers contentEditable may synthesize when the user
// presses Enter. Serialization treats their boundaries as newlines.
const BLOCK_TAGS = new Set(['DIV', 'P']);

// The `<br>` the browser leaves as the last child of a block (e.g.
// `<div><br></div>` for an empty line, or a trailing placeholder) is not a
// real line break — the block boundary already supplies the newline. All
// three DOM walkers (serialize, caret flattening, offset mapping) ignore it
// so the stored string, trigger detection, and insertion stay in lock-step.
function isTrailingPlaceholderBr(node: Node, parent: Node): boolean {
    return (
        node instanceof HTMLElement &&
        node.tagName === 'BR' &&
        node === parent.lastChild
    );
}

const PILL_CLASS =
    'inline-flex items-center rounded bg-blue-50 px-1 align-middle ' +
    'text-[13px] font-medium whitespace-nowrap text-blue-700 select-none ' +
    'dark:bg-blue-950/50 dark:text-blue-300';
const PILL_ICON_CLASS =
    'inline-flex size-3.5 shrink-0 items-center justify-center';
const DROP_PREVIEW_CLASS =
    'mx-0.5 inline-flex items-center rounded border border-blue-400 ' +
    'bg-blue-100 px-1 align-middle text-[12px] font-semibold ' +
    'pointer-events-none whitespace-nowrap text-blue-700 shadow-sm ' +
    'ring-2 ring-blue-200 select-none dark:border-blue-500 dark:bg-blue-950/70 ' +
    'dark:text-blue-200 dark:ring-blue-900';
const DRAGGING_PILL_CLASS = 'opacity-45';
const TYPING_HISTORY_COALESCE_MS = 1000;
const CHARACTERS_WITHOUT_LEADING_SPACE = new Set([
    ',',
    '.',
    ';',
    ':',
    '!',
    '?',
    ')',
    ']',
    '}',
    "'",
    '’',
]);
const CHARACTERS_WITHOUT_TRAILING_SPACE = new Set(['(', '[', '{', "'", '‘']);

// Matches a `{token}` placeholder. Only tokens in the known-variable set
// are turned into pills; anything else stays literal text.
const TOKEN_REGEX = /\{([a-z_]+)\}/g;

function serializeNode(node: Node, isFirstTopLevel: boolean): string {
    if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent ?? '';
    }
    if (node instanceof HTMLElement) {
        if (node.dataset[DROP_PREVIEW_DATA_KEY]) {
            return '';
        }
        const token = node.dataset[VARIABLE_DATA_KEY];
        if (token) {
            return `{${token}}`;
        }
        if (node.tagName === 'BR') {
            return '\n';
        }
        const isBlock = BLOCK_TAGS.has(node.tagName);
        const inner = Array.from(node.childNodes)
            .filter(
                (child) => !(isBlock && isTrailingPlaceholderBr(child, node)),
            )
            .map((child) => serializeNode(child, false))
            .join('');
        // A new block wrapper after existing content represents a line
        // break; the first top-level block does not (it is just the
        // initial line).
        if (isBlock && !isFirstTopLevel) {
            return `\n${inner}`;
        }
        return inner;
    }
    return '';
}

// Reads a contentEditable editor back into the canonical `{token}`
// string. Pills become `{token}`, `<br>` and block boundaries become
// newlines, everything else is its text content.
export function serializeVariableEditor(editor: HTMLElement): string {
    return Array.from(editor.childNodes)
        .map((node, index) => serializeNode(node, index === 0))
        .join('');
}

function createPill(
    token: string,
    text: string,
    iconMarkup?: string,
    draggable = false,
): HTMLSpanElement {
    const pill = document.createElement('span');
    pill.contentEditable = 'false';
    if (draggable) {
        pill.draggable = true;
    }
    pill.dataset[VARIABLE_DATA_KEY] = token;
    pill.className = cn(
        PILL_CLASS,
        iconMarkup ? 'gap-1' : null,
        draggable ? 'cursor-grab active:cursor-grabbing' : null,
    );

    if (iconMarkup) {
        const iconContainer = document.createElement('span');
        iconContainer.className = PILL_ICON_CLASS;
        iconContainer.setAttribute('aria-hidden', 'true');
        iconContainer.innerHTML = iconMarkup;
        pill.appendChild(iconContainer);
    }

    const label = document.createElement('span');
    label.dataset[VARIABLE_LABEL_DATA_KEY] = 'true';
    label.textContent = text;
    pill.appendChild(label);
    return pill;
}

function createDropPreview(text: string): HTMLSpanElement {
    const preview = document.createElement('span');
    preview.contentEditable = 'false';
    preview.dataset[DROP_PREVIEW_DATA_KEY] = 'true';
    preview.className = DROP_PREVIEW_CLASS;
    preview.textContent = text;
    preview.setAttribute('aria-hidden', 'true');
    return preview;
}

// Rebuilds the editor DOM from a stored `{token}` string. Known tokens
// become pills (their visible text resolved via `getPillText`); unknown
// `{...}` sequences are left as literal text so nothing is silently
// dropped.
export function populateVariableEditor(
    editor: HTMLElement,
    value: string,
    getPillText: (token: string) => string,
    knownTokens: ReadonlySet<string>,
    getPillIconMarkup?: (token: string) => string | undefined,
    draggablePills = false,
): void {
    editor.innerHTML = '';
    let lastIndex = 0;
    TOKEN_REGEX.lastIndex = 0;
    let match = TOKEN_REGEX.exec(value);
    while (match !== null) {
        const token = match[1] ?? '';
        if (knownTokens.has(token)) {
            if (match.index > lastIndex) {
                editor.appendChild(
                    document.createTextNode(
                        value.slice(lastIndex, match.index),
                    ),
                );
            }
            editor.appendChild(
                createPill(
                    token,
                    getPillText(token),
                    getPillIconMarkup?.(token),
                    draggablePills,
                ),
            );
            lastIndex = TOKEN_REGEX.lastIndex;
        }
        match = TOKEN_REGEX.exec(value);
    }
    if (lastIndex < value.length) {
        editor.appendChild(document.createTextNode(value.slice(lastIndex)));
    }
}

// Flattens the editor into a plain string + caret offset, with each pill
// counted as a single opaque character. Used to locate the `@` trigger
// relative to the caret without the pills shifting offsets.
const PILL_PLACEHOLDER = '￼';

// Recurses so block wrappers (DIV/P) the browser inserts on Enter are
// flattened with newline boundaries — matching serializeVariableEditor —
// rather than treated as one opaque node. Without recursion a `@` on any
// line after the first reads as following the previous line's last
// character, so the trigger is missed and insertion maps to the wrong
// offset.
export function getEditorTextAndCaret(editor: HTMLElement): {
    readonly text: string;
    readonly caret: number;
} {
    const selection = window.getSelection();
    const caretNode =
        selection && selection.rangeCount > 0
            ? selection.getRangeAt(0).startContainer
            : null;
    const caretOffset =
        selection && selection.rangeCount > 0
            ? selection.getRangeAt(0).startOffset
            : 0;

    let text = '';
    let caret = -1;

    const walk = (parent: Node, skipTrailingBr: boolean): void => {
        for (const node of parent.childNodes) {
            // Ignore a block's trailing placeholder <br> exactly as
            // serializeVariableEditor does, so this flattened string never
            // grows longer than the stored value.
            if (skipTrailingBr && isTrailingPlaceholderBr(node, parent)) {
                continue;
            }
            if (node.nodeType === Node.TEXT_NODE) {
                if (node === caretNode) {
                    caret = text.length + caretOffset;
                }
                text += node.textContent ?? '';
            } else if (node instanceof HTMLElement) {
                if (node.dataset[DROP_PREVIEW_DATA_KEY]) {
                    continue;
                }
                if (node.dataset[VARIABLE_DATA_KEY]) {
                    if (caretNode && node.contains(caretNode)) {
                        caret = text.length + 1;
                    }
                    text += PILL_PLACEHOLDER;
                } else if (node.tagName === 'BR') {
                    if (node === caretNode) {
                        caret = text.length;
                    }
                    text += '\n';
                } else if (BLOCK_TAGS.has(node.tagName)) {
                    // A block wrapper after existing content starts a new
                    // line; recurse so its inner text/pills land at the
                    // right offsets.
                    if (text.length > 0) {
                        text += '\n';
                    }
                    walk(node, true);
                } else {
                    walk(node, false);
                }
            }
        }
    };
    walk(editor, false);

    if (caret === -1 && caretNode === editor) {
        caret = text.length;
    }
    return { text, caret };
}

// Variable labels are multi-word (`caregiver_first_name` -> "caregiver
// first name"), so the query may contain spaces. Three covers the longest
// registered labels ("time and attendance pin", "shift day of week"); a
// fourth space reads as a sentence, not a variable search.
const MAX_QUERY_SPACES = 3;

// Scans backwards from the caret for the nearest `@` trigger. To avoid
// firing inside a word or an email address, `@` only counts at the start
// of the line, after whitespace, or immediately after a pill (which
// flattens to PILL_PLACEHOLDER) so a variable can be inserted right after
// another one. Crosses up to MAX_QUERY_SPACES spaces so multi-word labels
// stay searchable; stops at line breaks, and a query starting with
// whitespace (`@ foo`) is not a search attempt. Returns the `@` index, or
// -1 when the caret is not inside an open `@…` token.
export function findTriggerIndex(text: string, caret: number): number {
    let spaceCount = 0;
    for (let i = caret - 1; i >= 0; i--) {
        const ch = text[i];
        if (ch === undefined || ch === '\n') {
            return -1;
        }
        if (ch === '@') {
            const prev = text[i - 1] ?? '';
            if (i !== 0 && !/\s/.test(prev) && prev !== PILL_PLACEHOLDER) {
                return -1;
            }
            const afterTrigger = text[i + 1];
            if (afterTrigger !== undefined && /\s/.test(afterTrigger)) {
                return -1;
            }
            return i;
        }
        if (/\s/.test(ch)) {
            spaceCount += 1;
            if (spaceCount > MAX_QUERY_SPACES) {
                return -1;
            }
        }
    }
    return -1;
}

// Locates the text node + offset for a flat index produced by
// getEditorTextAndCaret, recursing into block wrappers with the same
// newline/pill accounting so insertion maps to the right node on any line.
export function locateOffset(
    editor: HTMLElement,
    targetIndex: number,
): { readonly node: Text; readonly offset: number } | null {
    let consumed = 0;
    let result: { readonly node: Text; readonly offset: number } | null = null;

    const walk = (parent: Node, skipTrailingBr: boolean): void => {
        for (const node of parent.childNodes) {
            if (result) {
                return;
            }
            // Match serialize/caret flattening: a block's trailing
            // placeholder <br> consumes no characters.
            if (skipTrailingBr && isTrailingPlaceholderBr(node, parent)) {
                continue;
            }
            if (node.nodeType === Node.TEXT_NODE) {
                const len = node.textContent?.length ?? 0;
                if (consumed + len >= targetIndex) {
                    result = {
                        node: node as Text,
                        offset: targetIndex - consumed,
                    };
                    return;
                }
                consumed += len;
            } else if (node instanceof HTMLElement) {
                if (node.dataset[DROP_PREVIEW_DATA_KEY]) {
                    continue;
                }
                if (node.dataset[VARIABLE_DATA_KEY] || node.tagName === 'BR') {
                    consumed += 1;
                } else if (BLOCK_TAGS.has(node.tagName)) {
                    if (consumed > 0) {
                        consumed += 1;
                    }
                    walk(node, true);
                } else {
                    walk(node, false);
                }
            }
        }
    };
    walk(editor, false);
    return result;
}

type CaretPosition = {
    readonly left: number;
    readonly top: number;
};

type DocumentWithCaretRange = Document & {
    readonly caretRangeFromPoint?: (x: number, y: number) => Range | null;
    readonly caretPositionFromPoint?: (
        x: number,
        y: number,
    ) => { readonly offsetNode: Node; readonly offset: number } | null;
};

type DropCandidate = {
    readonly range: Range;
    readonly distance: number;
};

type DropPreviewLocation = {
    readonly node: Node;
    readonly offset: number;
    readonly label: string;
};

function setVariableDragData(
    event: React.DragEvent<HTMLElement>,
    variable: TemplateVariable,
): void {
    setVariableDragToken(event, variable.token, 'copy');
}

function setVariableDragToken(
    event: React.DragEvent<HTMLElement>,
    token: string,
    effectAllowed: 'copy' | 'copyMove',
): void {
    event.dataTransfer.effectAllowed = effectAllowed;
    event.dataTransfer.setData(TEMPLATE_VARIABLE_DRAG_TYPE, token);
    event.dataTransfer.setData('text/plain', `{${token}}`);
}

function getDataTransferText(dataTransfer: DataTransfer, type: string): string {
    try {
        return dataTransfer.getData(type);
    } catch {
        return '';
    }
}

function getDraggedToken(dataTransfer: DataTransfer): string | null {
    const draggedToken = getDataTransferText(
        dataTransfer,
        TEMPLATE_VARIABLE_DRAG_TYPE,
    );
    if (draggedToken) {
        return draggedToken;
    }

    const text = getDataTransferText(dataTransfer, 'text/plain');
    const tokenMatch = text.match(/^\{([a-z_]+)\}$/);
    return tokenMatch?.[1] ?? null;
}

function acceptsDropData(dataTransfer: DataTransfer): boolean {
    const types = Array.from(dataTransfer.types ?? []);
    return (
        types.length === 0 ||
        types.includes(TEMPLATE_VARIABLE_DRAG_TYPE) ||
        types.includes('text/plain') ||
        types.includes('Text')
    );
}

function distanceToRect(rect: DOMRect, x: number, y: number): number {
    const dx =
        x < rect.left ? rect.left - x : x > rect.right ? x - rect.right : 0;
    const dy =
        y < rect.top ? rect.top - y : y > rect.bottom ? y - rect.bottom : 0;
    return dx * dx + dy * dy;
}

function isUsefulRect(rect: DOMRect): boolean {
    return rect.width > 0 || rect.height > 0;
}

function chooseCloserCandidate(
    current: DropCandidate | null,
    next: DropCandidate | null,
): DropCandidate | null {
    if (!next) {
        return current;
    }
    if (!current || next.distance < current.distance) {
        return next;
    }
    return current;
}

function getTextDropCandidate(
    node: Text,
    x: number,
    y: number,
): DropCandidate | null {
    const text = node.textContent ?? '';
    if (!text) {
        return null;
    }

    let best: DropCandidate | null = null;
    for (let index = 0; index < text.length; index += 1) {
        const characterRange = node.ownerDocument.createRange();
        characterRange.setStart(node, index);
        characterRange.setEnd(node, index + 1);
        if (typeof characterRange.getClientRects !== 'function') {
            continue;
        }
        for (const rect of Array.from(characterRange.getClientRects())) {
            if (!isUsefulRect(rect)) {
                continue;
            }
            const offset = x <= rect.left + rect.width / 2 ? index : index + 1;
            const range = node.ownerDocument.createRange();
            range.setStart(node, offset);
            range.collapse(true);
            best = chooseCloserCandidate(best, {
                range,
                distance: distanceToRect(rect, x, y),
            });
        }
    }
    return best;
}

function getElementBoundaryDropCandidate(
    element: HTMLElement,
    x: number,
    y: number,
): DropCandidate | null {
    const rect = element.getBoundingClientRect();
    if (!isUsefulRect(rect)) {
        return null;
    }

    const range = element.ownerDocument.createRange();
    if (x <= rect.left + rect.width / 2) {
        range.setStartBefore(element);
    } else {
        range.setStartAfter(element);
    }
    range.collapse(true);
    return { range, distance: distanceToRect(rect, x, y) };
}

function getNearestDropRangeFromPoint(
    editor: HTMLElement,
    x: number,
    y: number,
): Range | null {
    const best: { current: DropCandidate | null } = { current: null };

    const walk = (parent: Node): void => {
        for (const node of parent.childNodes) {
            if (node.nodeType === Node.TEXT_NODE) {
                best.current = chooseCloserCandidate(
                    best.current,
                    getTextDropCandidate(node as Text, x, y),
                );
                continue;
            }
            if (!(node instanceof HTMLElement)) {
                continue;
            }
            if (node.dataset[DROP_PREVIEW_DATA_KEY]) {
                continue;
            }
            if (
                node.dataset[VARIABLE_DATA_KEY] ||
                node.dataset[GENERATED_SEPARATOR_DATA_KEY] ||
                node.tagName === 'BR'
            ) {
                best.current = chooseCloserCandidate(
                    best.current,
                    getElementBoundaryDropCandidate(node, x, y),
                );
                continue;
            }
            walk(node);
        }
    };
    walk(editor);

    return best.current ? best.current.range : null;
}

function isInsideDropPreview(node: Node): boolean {
    const element = node instanceof Element ? node : node.parentElement;
    return element?.closest('[data-variable-drop-preview="true"]') !== null;
}

function getVariablePill(node: EventTarget | null): HTMLElement | null {
    const element =
        node instanceof Element
            ? node
            : node instanceof Node
              ? node.parentElement
              : null;
    return element?.closest<HTMLElement>('[data-variable]') ?? null;
}

function isInsideAtomicContent(node: Node): boolean {
    const element = node instanceof Element ? node : node.parentElement;
    return (
        (element?.closest(
            '[data-variable], [data-generated-variable-separator]',
        ) ?? null) !== null
    );
}

type AdjacentBoundary = {
    readonly character: string;
    readonly textNode: Text | null;
};

function getAdjacentBoundary(
    element: HTMLElement,
    side: 'before' | 'after',
): AdjacentBoundary | null {
    let sibling =
        side === 'before' ? element.previousSibling : element.nextSibling;
    while (sibling) {
        if (sibling instanceof Text) {
            if (sibling.data.length > 0) {
                return {
                    character:
                        side === 'before'
                            ? (sibling.data[sibling.data.length - 1] ?? '')
                            : (sibling.data[0] ?? ''),
                    textNode: sibling,
                };
            }
            sibling =
                side === 'before'
                    ? sibling.previousSibling
                    : sibling.nextSibling;
            continue;
        }
        if (
            sibling instanceof HTMLElement &&
            sibling.dataset[GENERATED_SEPARATOR_DATA_KEY]
        ) {
            return { character: ' ', textNode: null };
        }
        if (
            sibling instanceof HTMLElement &&
            (sibling.tagName === 'BR' || BLOCK_TAGS.has(sibling.tagName))
        ) {
            return { character: '\n', textNode: null };
        }
        return { character: '\uFFFC', textNode: null };
    }
    return null;
}

function isInlineSeparator(character: string): boolean {
    return character === ' ' || character === '\t' || character === '\u00A0';
}

function removeBoundaryCharacter(
    boundary: AdjacentBoundary,
    side: 'before' | 'after',
): void {
    const textNode = boundary.textNode;
    if (!textNode) {
        return;
    }
    const offset = side === 'before' ? textNode.data.length - 1 : 0;
    textNode.deleteData(offset, 1);
}

function removePillSourceSeparator(
    pill: HTMLElement,
    editor: HTMLElement,
): void {
    // Keep each generated space attached to its owner even if the user
    // types between it and the pill before dragging again.
    const separatorOwner = pill.dataset[VARIABLE_SEPARATORS_DATA_KEY];
    if (separatorOwner !== undefined) {
        for (const separator of editor.querySelectorAll<HTMLElement>(
            '[data-generated-variable-separator]',
        )) {
            if (
                separator.dataset[GENERATED_SEPARATOR_DATA_KEY] ===
                separatorOwner
            ) {
                separator.remove();
            }
        }
        return;
    }
    const before = getAdjacentBoundary(pill, 'before');
    const after = getAdjacentBoundary(pill, 'after');
    const hasSeparatorBefore =
        before !== null && isInlineSeparator(before.character);
    const hasSeparatorAfter =
        after !== null && isInlineSeparator(after.character);

    if (
        (before === null || before.character === '\n') &&
        hasSeparatorAfter &&
        after
    ) {
        removeBoundaryCharacter(after, 'after');
    } else if (
        (after === null || after.character === '\n') &&
        hasSeparatorBefore &&
        before
    ) {
        removeBoundaryCharacter(before, 'before');
    } else if (hasSeparatorBefore && hasSeparatorAfter && after) {
        removeBoundaryCharacter(after, 'after');
    } else if (
        hasSeparatorBefore &&
        before &&
        after &&
        CHARACTERS_WITHOUT_LEADING_SPACE.has(after.character)
    ) {
        removeBoundaryCharacter(before, 'before');
    } else if (
        hasSeparatorAfter &&
        before &&
        after &&
        CHARACTERS_WITHOUT_TRAILING_SPACE.has(before.character)
    ) {
        removeBoundaryCharacter(after, 'after');
    }
}

function addPillDestinationSeparators(pill: HTMLElement): void {
    const before = getAdjacentBoundary(pill, 'before');
    const after = getAdjacentBoundary(pill, 'after');
    const ownerDocument = pill.ownerDocument;
    const separatorOwner =
        pill.dataset[VARIABLE_SEPARATORS_DATA_KEY] ?? crypto.randomUUID();
    pill.dataset[VARIABLE_SEPARATORS_DATA_KEY] = separatorOwner;
    const createSeparator = () => {
        const separator = ownerDocument.createElement('span');
        separator.dataset[GENERATED_SEPARATOR_DATA_KEY] = separatorOwner;
        // Make the generated character atomic so typing goes beside it.
        // It still serializes as an ordinary space in the message.
        separator.contentEditable = 'false';
        separator.textContent = ' ';
        return separator;
    };

    if (
        before !== null &&
        !/\s/u.test(before.character) &&
        !CHARACTERS_WITHOUT_TRAILING_SPACE.has(before.character)
    ) {
        pill.before(createSeparator());
    }
    if (
        after !== null &&
        !/\s/u.test(after.character) &&
        !CHARACTERS_WITHOUT_LEADING_SPACE.has(after.character)
    ) {
        pill.after(createSeparator());
    }
}

function getNativeDropRangeFromPoint(
    event: React.DragEvent<HTMLElement>,
    editor: HTMLElement,
): Range | null {
    const ownerDocument = editor.ownerDocument as DocumentWithCaretRange;
    const range = ownerDocument.caretRangeFromPoint?.(
        event.clientX,
        event.clientY,
    );
    if (
        range &&
        (range.commonAncestorContainer === editor ||
            editor.contains(range.commonAncestorContainer)) &&
        !isInsideDropPreview(range.startContainer) &&
        !isInsideAtomicContent(range.startContainer)
    ) {
        return range;
    }

    const position = ownerDocument.caretPositionFromPoint?.(
        event.clientX,
        event.clientY,
    );
    if (
        !position ||
        !(
            position.offsetNode === editor ||
            editor.contains(position.offsetNode)
        ) ||
        isInsideDropPreview(position.offsetNode) ||
        isInsideAtomicContent(position.offsetNode)
    ) {
        return null;
    }

    const nextRange = ownerDocument.createRange();
    nextRange.setStart(position.offsetNode, position.offset);
    nextRange.collapse(true);
    return nextRange;
}

function getDropRangeFromPoint(
    event: React.DragEvent<HTMLElement>,
    editor: HTMLElement,
): Range | null {
    const nativeRange = getNativeDropRangeFromPoint(event, editor);
    if (nativeRange) {
        return nativeRange;
    }

    const nearestRange = getNearestDropRangeFromPoint(
        editor,
        event.clientX,
        event.clientY,
    );
    if (nearestRange) {
        return nearestRange;
    }
    return null;
}

// Caret position relative to the editor's positioned wrapper, so the
// dropdown can anchor directly under the character being typed. Returns
// null when the caret rect can't be measured (e.g. jsdom has no layout
// engine), in which case the dropdown falls back to a fixed corner.
function getCaretPosition(editor: HTMLElement): CaretPosition | null {
    const wrapper = editor.parentElement;
    const selection = window.getSelection();
    if (!wrapper || !selection || selection.rangeCount === 0) {
        return null;
    }
    // Range rect APIs are layout-dependent and absent in non-layout
    // environments (jsdom), so any failure just drops to the fallback
    // corner rather than breaking input handling.
    try {
        const range = selection.getRangeAt(0).cloneRange();
        range.collapse(true);
        let rect = range.getBoundingClientRect();
        if (rect.left === 0 && rect.top === 0 && rect.height === 0) {
            // A collapsed range can report an empty bounding rect; fall
            // back to its first client rect when the browser provides one.
            const first = range.getClientRects().item(0);
            if (!first) {
                return null;
            }
            rect = first;
        }
        const wrapperRect = wrapper.getBoundingClientRect();
        return {
            left: rect.left - wrapperRect.left,
            top: rect.bottom - wrapperRect.top + 4,
        };
    } catch {
        return null;
    }
}

function placeCaretAtEnd(editor: HTMLElement): void {
    const selection = window.getSelection();
    if (!selection) {
        return;
    }

    const range = editor.ownerDocument.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
}

type DropdownState = {
    readonly open: boolean;
    readonly query: string;
    readonly triggerIndex: number;
    readonly caretIndex: number;
};

type HistoryKind = 'typing' | 'structural';

type EditorSnapshot = {
    readonly value: string;
    readonly contents: DocumentFragment;
};

function takeEditorSnapshot(editor: HTMLElement): EditorSnapshot {
    // Clone the editable DOM so undo preserves generated-space ownership
    // through adjacent typing, text-node splits, and browser line wrappers.
    const contents = editor.ownerDocument.createDocumentFragment();
    contents.append(
        ...Array.from(editor.childNodes, (node) => node.cloneNode(true)),
    );
    contents
        .querySelectorAll('[data-variable-drop-preview]')
        .forEach((preview) => preview.remove());
    return {
        value: serializeVariableEditor(editor),
        contents,
    };
}

const CLOSED_DROPDOWN: DropdownState = {
    open: false,
    query: '',
    triggerIndex: -1,
    caretIndex: -1,
};

// A variable matches the in-progress `@query` if its label or token
// contains it (an empty query matches everything). Shared by the rendered
// list and the open/close decision so the two never disagree.
function variableMatches(variable: TemplateVariable, query: string): boolean {
    const normalized = query.toLowerCase();
    return (
        normalized === '' ||
        variable.label.toLowerCase().includes(normalized) ||
        variable.token.includes(normalized) ||
        variable.description?.toLowerCase().includes(normalized) === true
    );
}

function VariableDropdown({
    open,
    items,
    activeIndex,
    position,
    previewValues,
    variableIcons,
    onActiveIndexChange,
    onSelect,
}: {
    readonly open: boolean;
    readonly items: ReadonlyArray<TemplateVariable>;
    readonly activeIndex: number;
    readonly position: CaretPosition | null;
    readonly previewValues?: Readonly<Record<string, string>>;
    readonly variableIcons?: Readonly<Record<string, ReactNode>>;
    readonly onActiveIndexChange: (index: number) => void;
    readonly onSelect: (token: string) => void;
}) {
    if (!open || items.length === 0) {
        return null;
    }
    return (
        <div
            data-variable-dropdown=""
            role="listbox"
            // Keep the editor focused and its caret in place while the
            // item is clicked — a focus shift would collapse the caret
            // before onClick can run the insertion.
            onMouseDown={(event) => {
                event.preventDefault();
            }}
            style={
                position
                    ? { left: position.left, top: position.top }
                    : undefined
            }
            className={cn(
                'border-border bg-popover text-popover-foreground absolute z-50 w-[34rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border py-1.5 shadow-lg',
                // Anchor under the caret when measured; otherwise pin to
                // the editor's top-left corner.
                position ? null : 'top-full left-0 mt-1',
            )}
        >
            {items.map((variable, index) => {
                const preview = previewValues?.[variable.token];
                const icon = variableIcons?.[variable.token];
                const activateOption = () => onActiveIndexChange(index);
                return (
                    <button
                        key={variable.token}
                        type="button"
                        role="option"
                        aria-selected={index === activeIndex}
                        aria-label={
                            preview
                                ? `${variable.label}: ${preview}`
                                : variable.label
                        }
                        onFocus={activateOption}
                        onMouseEnter={activateOption}
                        onMouseMove={activateOption}
                        onMouseDown={(event) => {
                            event.preventDefault();
                        }}
                        onPointerDown={(event) => {
                            event.preventDefault();
                            onSelect(variable.token);
                        }}
                        onPointerEnter={activateOption}
                        onPointerMove={activateOption}
                        onClick={(event) => {
                            if (event.detail === 0) {
                                onSelect(variable.token);
                            }
                        }}
                        className={cn(
                            'hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground grid w-full cursor-pointer grid-cols-[minmax(0,1fr)_minmax(8rem,12rem)] items-center gap-4 px-3 py-2.5 text-left text-sm transition-colors focus-visible:outline-none',
                            index === activeIndex
                                ? 'bg-accent text-accent-foreground'
                                : 'text-foreground',
                        )}
                    >
                        <span className="flex min-w-0 items-center gap-2">
                            {icon ? (
                                <span className="flex size-5 shrink-0 items-center justify-center text-blue-700 dark:text-blue-300">
                                    {icon}
                                </span>
                            ) : null}
                            <span className="flex min-w-0 flex-col">
                                <span className="truncate rounded bg-blue-50 px-1.5 py-0.5 text-[13px] leading-5 font-medium text-blue-700 dark:bg-blue-950/50 dark:text-blue-300">
                                    {variable.label}
                                </span>
                                {variable.description ? (
                                    <span className="text-muted-foreground mt-0.5 truncate text-xs">
                                        {variable.description}
                                    </span>
                                ) : null}
                            </span>
                        </span>
                        {preview ? (
                            <span
                                className={cn(
                                    'min-w-0 truncate text-right text-sm leading-5',
                                    index === activeIndex
                                        ? 'text-accent-foreground/70'
                                        : 'text-muted-foreground',
                                )}
                            >
                                {preview}
                            </span>
                        ) : null}
                    </button>
                );
            })}
        </div>
    );
}

type Props = {
    readonly value: string;
    readonly onChange: (next: string) => void;
    // Resolved value rendered inside the `{display_name}` pill. Updates
    // live as the admin edits the display-name field above the editor.
    // Onboarding-specific; omit on surfaces without a `{display_name}`
    // token.
    readonly displayNameValue?: string;
    readonly variables?: ReadonlyArray<TemplateVariable>;
    readonly placeholder?: string;
    readonly ariaLabel?: string;
    readonly id?: string;
    // Forwarded to the editable element so a wrapping form (e.g. shadcn
    // FormControl/FormMessage) can connect its label and link validation
    // errors to this input.
    readonly ariaDescribedby?: string;
    readonly ariaInvalid?: boolean;
    readonly readOnly?: boolean;
    readonly className?: string;
    // Merged onto the editable element after the defaults, so compact
    // hosts (e.g. the chat approval card) can shrink the box and type
    // scale without restating the editor behavior.
    readonly editorClassName?: string;
    readonly variablePreviewValues?: Readonly<Record<string, string>>;
    readonly variableIcons?: Readonly<Record<string, ReactNode>>;
    readonly draggablePills?: boolean;
    readonly removePillsOnDragOut?: boolean;
    readonly snapVariableDropsToWords?: boolean;
    readonly actionRef?: Ref<VariableTextEditorActions>;
};

export type VariableTextEditorActions = Readonly<{
    readonly insertVariable: (token: string) => void;
}>;

export function VariableTextEditor({
    value,
    onChange,
    displayNameValue,
    variables = [],
    placeholder = '',
    ariaLabel,
    id,
    ariaDescribedby,
    ariaInvalid,
    readOnly = false,
    className,
    editorClassName,
    variablePreviewValues,
    variableIcons,
    draggablePills = false,
    removePillsOnDragOut = false,
    snapVariableDropsToWords = false,
    actionRef,
}: Props) {
    const editorRef = useRef<HTMLDivElement>(null);
    const lastSelectionRangeRef = useRef<Range | null>(null);
    const dropPreviewRef = useRef<HTMLSpanElement | null>(null);
    const draggedPillRef = useRef<HTMLElement | null>(null);
    const stopDragOutsideRef = useRef<(() => void) | null>(null);
    const historyRef = useRef<{
        past: Array<EditorSnapshot>;
        future: Array<EditorSnapshot>;
    }>({ past: [], future: [] });
    const historyGroupRef = useRef<{
        kind: HistoryKind;
        timestamp: number;
    } | null>(null);
    const dropPreviewLocationRef = useRef<DropPreviewLocation | null>(null);
    const [dropdown, setDropdown] = useState<DropdownState>(CLOSED_DROPDOWN);
    const [activeIndex, setActiveIndex] = useState(0);
    const [caretPos, setCaretPos] = useState<CaretPosition | null>(null);
    const pillsAreDraggable = draggablePills && !readOnly;

    const knownTokens = useMemo(
        () =>
            new Set(
                variables.flatMap((variable) => [
                    variable.token,
                    ...(variable.aliases ?? []),
                ]),
            ),
        [variables],
    );
    const variableRenderingSignature = useMemo(
        () =>
            variables
                .map((variable) =>
                    [
                        variable.token,
                        variable.label,
                        ...(variable.aliases ?? []),
                    ].join('\u0000'),
                )
                .join('\u0001'),
        [variables],
    );

    // Pill text: `display_name` resolves to the admin's live value;
    // every other variable shows its label as a placeholder for the
    // value substituted per-recipient at send time.
    const resolvedDisplayName = (displayNameValue ?? '').trim();
    const getPillText = (token: string): string => {
        if (token === 'display_name') {
            return resolvedDisplayName || 'display name';
        }
        return (
            variables.find(
                (variable) =>
                    variable.token === token ||
                    variable.aliases?.includes(token) === true,
            )?.label ?? token
        );
    };
    // Keep the latest resolver reachable from the value-sync effect
    // without making that effect depend on it (which would reseed the
    // DOM on every display-name keystroke and clobber the caret).
    const getPillTextRef = useRef(getPillText);
    getPillTextRef.current = getPillText;

    const variableIconMarkupByToken = useMemo<
        Readonly<Record<string, string>> | undefined
    >(() => {
        if (!variableIcons) {
            return undefined;
        }

        const entries: Array<[string, string]> = [];
        for (const [token, icon] of Object.entries(variableIcons)) {
            if (!icon) {
                continue;
            }
            const iconMarkup = renderToStaticMarkup(
                <Fragment>{icon}</Fragment>,
            );
            if (iconMarkup) {
                entries.push([token, iconMarkup]);
            }
        }
        return Object.fromEntries(entries);
    }, [variableIcons]);

    const getPillIconMarkup = (token: string): string | undefined =>
        variableIconMarkupByToken?.[token];
    const getPillIconMarkupRef = useRef(getPillIconMarkup);
    getPillIconMarkupRef.current = getPillIconMarkup;

    // Source-of-truth bridge: the contentEditable DOM is authoritative
    // while editing, but `value` is the React-owned string. We record
    // what we last emitted so the value-sync effect can tell a
    // self-originated update (skip reseed) from an external one (reseed).
    const lastSnapshotRef = useRef<EditorSnapshot | null>(null);
    const lastVariableIconMarkupByTokenRef = useRef(variableIconMarkupByToken);
    const lastVariableRenderingSignatureRef = useRef(
        variableRenderingSignature,
    );
    const lastPillsAreDraggableRef = useRef(pillsAreDraggable);

    function clearDropPreview() {
        const preview = dropPreviewRef.current;
        if (preview?.parentNode) {
            preview.parentNode.removeChild(preview);
        }
        dropPreviewRef.current = null;
        dropPreviewLocationRef.current = null;
    }

    function clearDraggedPill() {
        stopDragOutsideRef.current?.();
        stopDragOutsideRef.current = null;
        draggedPillRef.current?.classList.remove(DRAGGING_PILL_CLASS);
        draggedPillRef.current = null;
    }

    useEffect(() => () => stopDragOutsideRef.current?.(), []);

    useEffect(() => {
        const editor = editorRef.current;
        if (!editor) {
            return;
        }
        const iconMarkupChanged =
            variableIconMarkupByToken !==
            lastVariableIconMarkupByTokenRef.current;
        const variableRenderingChanged =
            variableRenderingSignature !==
            lastVariableRenderingSignatureRef.current;
        const draggablePillsChanged =
            pillsAreDraggable !== lastPillsAreDraggableRef.current;
        if (
            value === lastSnapshotRef.current?.value &&
            !iconMarkupChanged &&
            !variableRenderingChanged &&
            !draggablePillsChanged
        ) {
            return;
        }
        clearDropPreview();
        clearDraggedPill();
        lastSelectionRangeRef.current = null;
        populateVariableEditor(
            editor,
            value,
            getPillTextRef.current,
            knownTokens,
            getPillIconMarkupRef.current,
            pillsAreDraggable,
        );
        lastSnapshotRef.current = takeEditorSnapshot(editor);
        lastVariableIconMarkupByTokenRef.current = variableIconMarkupByToken;
        lastVariableRenderingSignatureRef.current = variableRenderingSignature;
        lastPillsAreDraggableRef.current = pillsAreDraggable;
        historyRef.current = { past: [], future: [] };
        historyGroupRef.current = null;
    }, [
        value,
        knownTokens,
        variableIconMarkupByToken,
        variableRenderingSignature,
        pillsAreDraggable,
    ]);

    // Live-update `{display_name}` pills in place when the resolved value
    // changes. Pills serialize to `{display_name}` regardless of their
    // visible text, so this never alters the stored string or fires
    // onChange.
    useEffect(() => {
        const editor = editorRef.current;
        if (!editor) {
            return;
        }
        const text = resolvedDisplayName || 'display name';
        const pills = editor.querySelectorAll<HTMLElement>(
            '[data-variable="display_name"]',
        );
        for (const pill of pills) {
            const label = pill.querySelector<HTMLElement>(
                '[data-variable-label="true"]',
            );
            if (label && label.textContent !== text) {
                label.textContent = text;
            } else if (!label && pill.textContent !== text) {
                pill.textContent = text;
            }
        }
    }, [resolvedDisplayName]);

    const filtered = useMemo(
        () =>
            variables.filter((variable) =>
                variableMatches(variable, dropdown.query),
            ),
        [dropdown.query, variables],
    );

    useEffect(() => {
        setActiveIndex(0);
    }, [dropdown.query, dropdown.open]);

    function pushUndoSnapshot(snapshot: EditorSnapshot) {
        const history = historyRef.current;
        if (history.past[history.past.length - 1]?.value === snapshot.value) {
            return;
        }
        history.past.push(snapshot);
        if (history.past.length > HISTORY_LIMIT) {
            history.past.shift();
        }
    }

    function applyEditorValue(snapshot: EditorSnapshot) {
        const editor = editorRef.current;
        if (!editor) {
            return;
        }
        clearDropPreview();
        editor.replaceChildren(snapshot.contents.cloneNode(true));
        // Restore current labels/icons rather than stale display-name text
        // or the transient dragging style captured in a history entry.
        editor
            .querySelectorAll<HTMLElement>('[data-variable]')
            .forEach((pill) => {
                const token = pill.dataset[VARIABLE_DATA_KEY];
                if (!token) {
                    return;
                }
                const restored = createPill(
                    token,
                    getPillTextRef.current(token),
                    getPillIconMarkupRef.current(token),
                    pillsAreDraggable,
                );
                const separatorOwner =
                    pill.dataset[VARIABLE_SEPARATORS_DATA_KEY];
                if (separatorOwner !== undefined) {
                    restored.dataset[VARIABLE_SEPARATORS_DATA_KEY] =
                        separatorOwner;
                }
                pill.replaceWith(restored);
            });
        lastSnapshotRef.current = takeEditorSnapshot(editor);
        onChange(snapshot.value);
        historyGroupRef.current = null;
        setDropdown(CLOSED_DROPDOWN);
        editor.focus();
        placeCaretAtEnd(editor);
    }

    function undoEditorChange(): boolean {
        const editor = editorRef.current;
        if (!editor) {
            return false;
        }

        const previous = historyRef.current.past.pop();
        if (previous === undefined) {
            return false;
        }

        const current = takeEditorSnapshot(editor);
        if (current.value !== previous.value) {
            historyRef.current.future.push(current);
        }
        applyEditorValue(previous);
        return true;
    }

    function redoEditorChange(): boolean {
        const editor = editorRef.current;
        if (!editor) {
            return false;
        }

        const next = historyRef.current.future.pop();
        if (next === undefined) {
            return false;
        }

        const current = takeEditorSnapshot(editor);
        if (current.value !== next.value) {
            pushUndoSnapshot(current);
        }
        applyEditorValue(next);
        return true;
    }

    function emitChange({
        historyKind = 'typing',
        recordHistory = true,
    }: {
        readonly historyKind?: HistoryKind;
        readonly recordHistory?: boolean;
    } = {}) {
        const editor = editorRef.current;
        if (!editor) {
            return;
        }
        const snapshot = takeEditorSnapshot(editor);
        const previous = lastSnapshotRef.current ?? snapshot;
        if (snapshot.value === previous.value) {
            lastSnapshotRef.current = snapshot;
            return;
        }
        if (recordHistory) {
            const now = Date.now();
            const lastGroup = historyGroupRef.current;
            const shouldCoalesce =
                historyKind === 'typing' &&
                lastGroup?.kind === 'typing' &&
                now - lastGroup.timestamp < TYPING_HISTORY_COALESCE_MS;
            if (!shouldCoalesce) {
                pushUndoSnapshot(previous);
            }
            historyGroupRef.current = { kind: historyKind, timestamp: now };
            historyRef.current.future = [];
        }
        // Record before onChange so the value-sync effect recognizes the
        // resulting prop update as our own and leaves the DOM alone.
        lastSnapshotRef.current = snapshot;
        onChange(snapshot.value);
    }

    function refreshDropdown() {
        const editor = editorRef.current;
        if (!editor) {
            return;
        }
        const { text, caret } = getEditorTextAndCaret(editor);
        const triggerIndex = findTriggerIndex(text, caret);
        if (triggerIndex === -1) {
            setDropdown(CLOSED_DROPDOWN);
            return;
        }
        const query = text.slice(triggerIndex + 1, caret);
        // Only open when at least one variable matches; an open-but-empty
        // dropdown renders nothing yet would still swallow Escape/Tab.
        if (!variables.some((variable) => variableMatches(variable, query))) {
            setDropdown(CLOSED_DROPDOWN);
            return;
        }
        setCaretPos(getCaretPosition(editor));
        setDropdown({ open: true, query, triggerIndex, caretIndex: caret });
    }

    function handleInput() {
        emitChange();
        refreshDropdown();
        rememberSelectionRange();
    }

    function rememberSelectionRange() {
        const editor = editorRef.current;
        const selection = window.getSelection();
        if (!editor || !selection || selection.rangeCount === 0) {
            return;
        }
        const range = selection.getRangeAt(0);
        if (
            range.commonAncestorContainer === editor ||
            editor.contains(range.commonAncestorContainer)
        ) {
            lastSelectionRangeRef.current = range.cloneRange();
        }
    }

    function insertVariableAtRange(
        token: string,
        range: Range,
        pillToMove?: HTMLElement,
    ) {
        const editor = editorRef.current;
        if (!editor) {
            return;
        }
        const selection = window.getSelection();
        if (!selection) {
            return;
        }

        range.deleteContents();
        // Anchor the destination before moving an existing node. DOM ranges
        // are live, so moving a pill that appears before the drop point can
        // otherwise shift the range and put it back in the wrong place.
        const destination = editor.ownerDocument.createTextNode('');
        range.insertNode(destination);
        let pill: HTMLElement;
        if (
            pillToMove &&
            (destination.previousSibling === pillToMove ||
                destination.nextSibling === pillToMove)
        ) {
            destination.remove();
            pill = pillToMove;
        } else {
            if (pillToMove) {
                removePillSourceSeparator(pillToMove, editor);
            }
            pill =
                pillToMove ??
                createPill(
                    token,
                    getPillText(token),
                    getPillIconMarkup(token),
                    pillsAreDraggable,
                );
            destination.replaceWith(pill);
            addPillDestinationSeparators(pill);
        }

        // Place the caret right after the pill (no forced trailing
        // space) so punctuation typed next sits flush against it.
        const afterRange = document.createRange();
        afterRange.setStartAfter(pill);
        afterRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(afterRange);
        lastSelectionRangeRef.current = afterRange.cloneRange();

        setDropdown(CLOSED_DROPDOWN);
        emitChange({ historyKind: 'structural' });
        editor.focus();
    }

    function insertVariableAtSavedSelection(token: string) {
        const editor = editorRef.current;
        if (readOnly || !editor || !knownTokens.has(token)) {
            return;
        }
        const savedRange = lastSelectionRangeRef.current;
        const insertionRange = document.createRange();
        if (
            savedRange !== null &&
            (savedRange.commonAncestorContainer === editor ||
                editor.contains(savedRange.commonAncestorContainer))
        ) {
            insertionRange.setStart(
                savedRange.startContainer,
                savedRange.startOffset,
            );
            insertionRange.setEnd(
                savedRange.endContainer,
                savedRange.endOffset,
            );
        } else {
            insertionRange.selectNodeContents(editor);
            insertionRange.collapse(false);
        }
        insertVariableAtRange(token, insertionRange);
    }

    useImperativeHandle(actionRef, () => ({
        insertVariable: insertVariableAtSavedSelection,
    }));

    function insertVariable(token: string) {
        const editor = editorRef.current;
        if (!editor) {
            return;
        }
        const trigger = locateOffset(editor, dropdown.triggerIndex);
        const caret = locateOffset(editor, dropdown.caretIndex);
        if (!trigger || !caret) {
            return;
        }

        // Remove the `@` trigger and any typed query.
        const deleteRange = document.createRange();
        deleteRange.setStart(trigger.node, trigger.offset);
        deleteRange.setEnd(caret.node, caret.offset);
        deleteRange.deleteContents();
        deleteRange.collapse(true);
        insertVariableAtRange(token, deleteRange);
    }

    function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
        if (
            (event.metaKey || event.ctrlKey) &&
            !event.altKey &&
            event.key.toLowerCase() === 'z'
        ) {
            event.preventDefault();
            if (event.shiftKey) {
                redoEditorChange();
            } else {
                undoEditorChange();
            }
            return;
        }

        // `refreshDropdown` only opens when at least one variable matches,
        // so an open dropdown always has selectable items; Escape/Tab must
        // dismiss it regardless.
        if (!dropdown.open) {
            return;
        }
        switch (event.key) {
            case 'Escape': {
                event.preventDefault();
                setDropdown(CLOSED_DROPDOWN);
                return;
            }
            case 'Tab': {
                event.preventDefault();
                setDropdown(CLOSED_DROPDOWN);
                return;
            }
            case 'Enter': {
                event.preventDefault();
                const choice = filtered[activeIndex] ?? filtered[0];
                if (choice) {
                    insertVariable(choice.token);
                }
                return;
            }
            case 'ArrowDown': {
                event.preventDefault();
                setActiveIndex((index) => (index + 1) % filtered.length);
                return;
            }
            case 'ArrowUp': {
                event.preventDefault();
                setActiveIndex(
                    (index) => (index - 1 + filtered.length) % filtered.length,
                );
                return;
            }
            default: {
                return;
            }
        }
    }

    function handlePaste(event: React.ClipboardEvent<HTMLDivElement>) {
        // Strip formatting; paste plain text only. execCommand is
        // deprecated but remains the only way to insert into a
        // contentEditable while preserving the native undo stack.
        event.preventDefault();
        const text = event.clipboardData.getData('text/plain');
        document.execCommand('insertText', false, text);
        emitChange({ historyKind: 'structural' });
        refreshDropdown();
    }

    function removeDraggedPill() {
        const editor = editorRef.current;
        const pill = draggedPillRef.current;
        if (!editor || !pill || !editor.contains(pill)) {
            clearDraggedPill();
            return;
        }

        clearDropPreview();
        removePillSourceSeparator(pill, editor);
        const caret = editor.ownerDocument.createRange();
        caret.setStartBefore(pill);
        caret.collapse(true);
        pill.remove();
        clearDraggedPill();
        setDropdown(CLOSED_DROPDOWN);
        emitChange({ historyKind: 'structural' });
        editor.focus();
        const selection = editor.ownerDocument.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(caret);
        rememberSelectionRange();
    }

    function handleVariableDragStart(event: React.DragEvent<HTMLDivElement>) {
        const editor = editorRef.current;
        const pill = getVariablePill(event.target);
        const token = pill?.dataset[VARIABLE_DATA_KEY];
        if (
            !pillsAreDraggable ||
            !editor ||
            !pill ||
            !editor.contains(pill) ||
            !token ||
            !knownTokens.has(token)
        ) {
            return;
        }

        // A pill moves inside its current editor but is copied when dropped
        // into another compatible editor.
        clearDraggedPill();
        setVariableDragToken(event, token, 'copyMove');
        draggedPillRef.current = pill;
        pill.classList.add(DRAGGING_PILL_CLASS);
        if (removePillsOnDragOut) {
            stopDragOutsideRef.current = trackVariableDragOutside(
                editor,
                removeDraggedPill,
            );
        }
    }

    function canUseSelectionFallbackForDrop(
        event: React.DragEvent<HTMLDivElement>,
        editor: HTMLElement,
    ): boolean {
        const rect = editor.getBoundingClientRect();
        if (!isUsefulRect(rect)) {
            return true;
        }

        return (
            event.clientX < rect.left ||
            event.clientX > rect.right ||
            event.clientY < rect.top ||
            event.clientY > rect.bottom
        );
    }

    function getInsertionRange(
        dropRange: Range | null,
        useSelectionFallback = true,
    ): Range {
        const editor = editorRef.current;
        const selection = window.getSelection();
        if (dropRange) {
            return dropRange;
        }
        if (
            useSelectionFallback &&
            editor &&
            selection &&
            selection.rangeCount > 0
        ) {
            const selectedRange = selection.getRangeAt(0);
            if (editor.contains(selectedRange.commonAncestorContainer)) {
                return selectedRange;
            }
        }

        const fallbackRange = document.createRange();
        if (editor) {
            fallbackRange.selectNodeContents(editor);
        }
        fallbackRange.collapse(false);
        return fallbackRange;
    }

    function getDropPreviewRange(): Range | null {
        const preview = dropPreviewRef.current;
        if (!preview?.parentNode) {
            return null;
        }

        const range = preview.ownerDocument.createRange();
        range.selectNode(preview);
        return range;
    }

    function getDropInsertionRange(
        event: React.DragEvent<HTMLDivElement>,
        editor: HTMLElement,
    ): Range {
        const range = getInsertionRange(
            getDropRangeFromPoint(event, editor),
            canUseSelectionFallbackForDrop(event, editor),
        );
        const isVariable =
            draggedPillRef.current !== null ||
            getDraggedToken(event.dataTransfer) !== null ||
            Array.from(event.dataTransfer.types ?? []).includes(
                TEMPLATE_VARIABLE_DRAG_TYPE,
            );
        return snapVariableDropsToWords && isVariable
            ? snapVariableDropToWord(
                  editor,
                  range,
                  event.clientX,
                  event.clientY,
              )
            : range;
    }

    function showDropPreviewAtRange(range: Range, label: string) {
        const insertionRange = range.cloneRange();
        insertionRange.collapse(true);
        const nextLocation: DropPreviewLocation = {
            node: insertionRange.startContainer,
            offset: insertionRange.startOffset,
            label,
        };
        const currentLocation = dropPreviewLocationRef.current;
        if (
            dropPreviewRef.current?.parentNode &&
            currentLocation?.node === nextLocation.node &&
            currentLocation.offset === nextLocation.offset &&
            currentLocation.label === label
        ) {
            return;
        }

        const previousPreview = dropPreviewRef.current;
        const preview = createDropPreview(label);
        insertionRange.insertNode(preview);
        dropPreviewRef.current = preview;
        dropPreviewLocationRef.current = nextLocation;
        if (previousPreview?.parentNode) {
            previousPreview.parentNode.removeChild(previousPreview);
        }
    }

    function getDropPreviewLabel(dataTransfer: DataTransfer): string {
        const token = getDraggedToken(dataTransfer);
        return token && knownTokens.has(token)
            ? getPillText(token)
            : 'Drop here';
    }

    function handleDragOver(event: React.DragEvent<HTMLDivElement>) {
        if (!acceptsDropData(event.dataTransfer)) {
            return;
        }

        event.preventDefault();
        event.dataTransfer.dropEffect =
            draggedPillRef.current === null ? 'copy' : 'move';

        const editor = editorRef.current;
        if (!editor) {
            return;
        }

        const insertionRange = getDropInsertionRange(event, editor);
        showDropPreviewAtRange(
            insertionRange,
            getDropPreviewLabel(event.dataTransfer),
        );
    }

    function handleDragLeave(event: React.DragEvent<HTMLDivElement>) {
        const relatedTarget = event.relatedTarget;
        if (
            relatedTarget instanceof Node &&
            event.currentTarget.contains(relatedTarget)
        ) {
            return;
        }

        clearDropPreview();
    }

    function handleDrop(event: React.DragEvent<HTMLDivElement>) {
        // Mirror paste: insert only the plain-text payload so dropped rich
        // content / HTML can't be injected into the contentEditable.
        event.preventDefault();
        const editor = editorRef.current;
        if (!editor) {
            return;
        }

        const insertionRange =
            getDropPreviewRange() ?? getDropInsertionRange(event, editor);

        const draggedToken = getDraggedToken(event.dataTransfer);
        if (draggedToken) {
            if (!knownTokens.has(draggedToken)) {
                clearDropPreview();
                clearDraggedPill();
                return;
            }
            const draggedPill = draggedPillRef.current;
            const pillToMove =
                draggedPill !== null &&
                editor.contains(draggedPill) &&
                draggedPill.dataset[VARIABLE_DATA_KEY] === draggedToken
                    ? draggedPill
                    : undefined;
            insertVariableAtRange(draggedToken, insertionRange, pillToMove);
            dropPreviewRef.current = null;
            dropPreviewLocationRef.current = null;
            clearDraggedPill();
            return;
        }

        const text = getDataTransferText(event.dataTransfer, 'text/plain');
        if (text) {
            insertionRange.deleteContents();
            const textNode = document.createTextNode(text);
            insertionRange.insertNode(textNode);
            const afterRange = document.createRange();
            afterRange.setStartAfter(textNode);
            afterRange.collapse(true);
            const selection = window.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(afterRange);
            emitChange({ historyKind: 'structural' });
            editor.focus();
            dropPreviewRef.current = null;
            dropPreviewLocationRef.current = null;
            clearDraggedPill();
            return;
        }

        clearDropPreview();
        clearDraggedPill();
    }

    const isEmpty = value.trim().length === 0;

    return (
        <div className={cn('relative', className)}>
            <div
                ref={editorRef}
                id={id}
                role="textbox"
                tabIndex={0}
                aria-multiline="true"
                aria-label={ariaLabel}
                aria-describedby={ariaDescribedby}
                aria-invalid={ariaInvalid}
                aria-readonly={readOnly || undefined}
                aria-placeholder={placeholder}
                aria-haspopup={readOnly ? undefined : 'listbox'}
                aria-expanded={readOnly ? undefined : dropdown.open}
                contentEditable={!readOnly}
                suppressContentEditableWarning={true}
                onInput={readOnly ? undefined : handleInput}
                onKeyDown={readOnly ? undefined : handleKeyDown}
                onKeyUp={readOnly ? undefined : rememberSelectionRange}
                onMouseUp={readOnly ? undefined : rememberSelectionRange}
                onFocus={readOnly ? undefined : rememberSelectionRange}
                onPaste={readOnly ? undefined : handlePaste}
                onDragStart={
                    pillsAreDraggable ? handleVariableDragStart : undefined
                }
                onDragOver={readOnly ? undefined : handleDragOver}
                onDragLeave={readOnly ? undefined : handleDragLeave}
                onDragEnd={
                    readOnly
                        ? undefined
                        : () => {
                              clearDropPreview();
                              clearDraggedPill();
                          }
                }
                onDrop={readOnly ? undefined : handleDrop}
                onBlur={() => {
                    rememberSelectionRange();
                    setDropdown(CLOSED_DROPDOWN);
                    clearDropPreview();
                }}
                className={cn(
                    'border-input focus-visible:border-ring focus-visible:ring-ring/50 dark:bg-input/30 min-h-24 w-full overflow-y-auto rounded-md border bg-transparent px-3 py-2 text-base break-words whitespace-pre-wrap shadow-xs outline-none focus-visible:ring-[3px] md:text-sm',
                    readOnly ? 'cursor-default opacity-70' : null,
                    editorClassName,
                )}
            />
            {isEmpty && placeholder ? (
                <div className="text-muted-foreground pointer-events-none absolute top-2 left-3 text-base md:text-sm">
                    {placeholder}
                </div>
            ) : null}
            <VariableDropdown
                open={!readOnly && dropdown.open}
                items={filtered}
                activeIndex={activeIndex}
                position={caretPos}
                previewValues={variablePreviewValues}
                variableIcons={variableIcons}
                onActiveIndexChange={setActiveIndex}
                onSelect={insertVariable}
            />
        </div>
    );
}

type VariableTextDisplayProps = {
    readonly value: string;
    readonly variables: ReadonlyArray<TemplateVariable>;
};

// Read-only render of a `{token}` template: known tokens become inline
// pills showing their label; all other text renders verbatim. The
// declarative counterpart to populateVariableEditor, used by the settings
// preview cards so a template reads the same way it does in the editor
// without being editable.
export function VariableTextDisplay({
    value,
    variables,
}: VariableTextDisplayProps) {
    const labelByToken = useMemo(
        () =>
            new Map(
                variables.flatMap((variable) => [
                    [variable.token, variable.label] as const,
                    ...(variable.aliases ?? []).map(
                        (alias) => [alias, variable.label] as const,
                    ),
                ]),
            ),
        [variables],
    );
    const nodes: Array<ReactNode> = [];
    let lastIndex = 0;
    let pillIndex = 0;
    TOKEN_REGEX.lastIndex = 0;
    let match = TOKEN_REGEX.exec(value);
    while (match !== null) {
        const label = labelByToken.get(match[1] ?? '');
        if (label !== undefined) {
            if (match.index > lastIndex) {
                nodes.push(value.slice(lastIndex, match.index));
            }
            nodes.push(
                <span key={pillIndex} className={PILL_CLASS}>
                    {label}
                </span>,
            );
            pillIndex += 1;
            lastIndex = TOKEN_REGEX.lastIndex;
        }
        match = TOKEN_REGEX.exec(value);
    }
    if (lastIndex < value.length) {
        nodes.push(value.slice(lastIndex));
    }
    return <Fragment>{nodes}</Fragment>;
}

type AvailableVariablesProps = {
    readonly variables: ReadonlyArray<TemplateVariable>;
    readonly groups?: ReadonlyArray<{
        readonly label: string;
        readonly variables: ReadonlyArray<TemplateVariable>;
    }>;
    readonly variableIcons?: Readonly<Record<string, ReactNode>>;
    readonly label?: string;
    /**
     * Render the grouped variables as a single vertical column instead of the
     * responsive multi-column grid. Use inside a narrow sticky sidebar so the
     * pills don't truncate.
     */
    readonly stacked?: boolean;
};

function VariableHelpTooltip() {
    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <button
                    type="button"
                    aria-label="How to use variables"
                    className="text-muted-foreground hover:text-foreground inline-flex size-4 items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:outline-none"
                >
                    <Info aria-hidden="true" className="size-3.5" />
                </button>
            </TooltipTrigger>
            <TooltipContent side="top">
                Use @ to tag a variable, or drag one into the editor.
            </TooltipContent>
        </Tooltip>
    );
}

// Read-only legend of the variables a template accepts. The helper text
// names the `@` trigger once so each pill can stay visually clean.
export function AvailableVariables({
    variables,
    groups,
    variableIcons,
    label = 'Available variables',
    stacked = false,
}: AvailableVariablesProps) {
    const variableGroups = groups ?? [{ label: '', variables }];
    const hasGroupedLayout = groups !== undefined;

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-1.5">
                <span className="text-muted-foreground text-xs">{label}:</span>
                <VariableHelpTooltip />
            </div>
            <div
                data-testid={
                    hasGroupedLayout ? 'available-variable-groups' : undefined
                }
                className={cn(
                    hasGroupedLayout
                        ? stacked
                            ? 'space-y-3'
                            : 'columns-1 gap-3 sm:columns-2 xl:columns-3'
                        : 'space-y-3',
                )}
            >
                {variableGroups.map((group, index) => (
                    <div
                        key={group.label || `variables-${index}`}
                        className={cn(
                            'min-w-0 space-y-1.5',
                            hasGroupedLayout && !stacked
                                ? 'mb-3 inline-block w-full break-inside-avoid'
                                : null,
                        )}
                    >
                        {group.label ? (
                            <div className="text-muted-foreground text-[11px] leading-none font-medium tracking-wide uppercase">
                                {group.label}
                            </div>
                        ) : null}
                        <div
                            role="list"
                            aria-label={
                                group.label ? `${group.label} variables` : label
                            }
                            className={cn(
                                hasGroupedLayout
                                    ? 'flex flex-col items-start gap-1.5'
                                    : 'flex flex-wrap items-center gap-1.5',
                            )}
                        >
                            {group.variables.map((variable) => (
                                <span
                                    key={variable.token}
                                    role="listitem"
                                    aria-label={variable.label}
                                    draggable={true}
                                    onDragStart={(event) =>
                                        setVariableDragData(event, variable)
                                    }
                                    className={cn(
                                        PILL_CLASS,
                                        'max-w-full cursor-grab gap-1 active:cursor-grabbing',
                                    )}
                                >
                                    {variableIcons?.[variable.token]}
                                    <span className="min-w-0 truncate">
                                        {variable.label}
                                    </span>
                                </span>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
